import { useState } from 'react';
import { Check, Sparkles, Info } from 'lucide-react';
import { FadeIn } from '../../components/ui/AnimatedSection';
import { useTranslation } from 'react-i18next';

type PackageType = 'essentiel' | 'signature' | 'intendance';

interface CalculatorProps {
  hideAuditButton?: boolean;
  vipMode?: boolean;
}

export default function Calculator({ hideAuditButton = false, vipMode = false }: CalculatorProps) {
  const { t } = useTranslation();
  const [selectedPackage, setSelectedPackage] = useState<PackageType>('essentiel');
  const [surface, setSurface] = useState(100);
  const [rooms, setRooms] = useState(3);
  const [bathrooms, setBathrooms] = useState(1);
  const [kitchens, setKitchens] = useState(1);
  
  const [specifics, setSpecifics] = useState({
    pets: false,
    children: false,
    art: false,
    marble: false,
    dressing: false,
    silver: false,
    groceries: false,
    plants: false
  });

  // Pricing Logic
  let baseHourlyRate = 50;
  if (selectedPackage === 'signature') baseHourlyRate = 65;
  if (selectedPackage === 'intendance') baseHourlyRate = 85;

  let weeklyHours = (surface / 20) + (bathrooms * 0.75) + (kitchens * 1);
  if (specifics.pets) weeklyHours += 1;
  if (specifics.children) weeklyHours += 0.5;
  if (specifics.art) weeklyHours += 1;
  if (specifics.marble) weeklyHours += 1.5;
  if (specifics.dressing) weeklyHours += 1.5;
  if (specifics.silver) weeklyHours += 1;
  if (specifics.groceries) weeklyHours += 1;
  if (specifics.plants) weeklyHours += 0.5;

  const monthlyHours = weeklyHours * 4;
  const grossMonthly = Math.round(monthlyHours * baseHourlyRate);
  const taxCredit = Math.round(grossMonthly / 2);

  const packages = [
    { 
      id: 'essentiel', 
      name: t('calculator.packages.essentiel.name'), 
      grossPrice: 50, 
      netPrice: 25,
      desc: t('calculator.packages.essentiel.desc') 
    },
    { 
      id: 'signature', 
      name: t('calculator.packages.signature.name'), 
      grossPrice: 65, 
      netPrice: 32.5,
      desc: t('calculator.packages.signature.desc') 
    },
    { 
      id: 'intendance', 
      name: t('calculator.packages.intendance.name'), 
      grossPrice: 85, 
      netPrice: 42.5,
      desc: t('calculator.packages.intendance.desc') 
    }
  ];

  return (
    <section className="py-32 bg-white relative" id="simulateur">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        <FadeIn className="mb-20">
          <span className="text-gold-600 tracking-[0.3em] text-xs font-bold uppercase mb-4 block">{t('calculator.sectionTag')}</span>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif text-black-deep mb-6">{t('calculator.sectionTitle')}</h2>
          <div className="w-24 h-px bg-gold-400"></div>
        </FadeIn>

        <div className="flex flex-col lg:flex-row gap-16 lg:gap-24 items-start">
          
          {/* LEFT COLUMN : CONFIGURATOR */}
          <div className="w-full lg:w-2/3 space-y-24">
            
            {/* STEP 1 */}
            <div className="relative">
              <svg className="absolute -left-12 top-0 hidden md:block pointer-events-none opacity-50 select-none" width="80" height="80" aria-hidden="true" focusable="false">
                <text x="0" y="60" fontFamily="serif" fontSize="60" fill="#e5e7eb">01</text>
              </svg>
              <h3 className="text-2xl font-serif mb-10 text-black-deep">{t('calculator.step1')}</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {packages.map(pkg => (
                  <div 
                    key={pkg.id}
                    onClick={() => setSelectedPackage(pkg.id as PackageType)}
                    className={`cursor-pointer group relative overflow-hidden transition-all duration-500 ${selectedPackage === pkg.id ? 'bg-black-deep text-white shadow-2xl scale-[1.02]' : 'bg-gray-50 text-black-deep hover:bg-gray-100'}`}
                  >
                    <div className="p-8 h-full flex flex-col">
                      {selectedPackage === pkg.id && (
                        <div className="absolute top-0 right-0 p-4">
                          <Check className="text-gold-400 w-5 h-5" />
                        </div>
                      )}
                      
                      <h4 className="font-serif text-xl mb-4">{pkg.name}</h4>
                      
                      <div className="mb-6 flex-1">
                        <div className="flex items-baseline">
                          <span className={`text-4xl font-serif ${selectedPackage === pkg.id ? 'text-gold-400' : 'text-black-deep'}`}>{pkg.netPrice}€</span>
                          <span className={`ml-2 text-sm ${selectedPackage === pkg.id ? 'text-gray-300' : 'text-gray-600'}`}>{t('calculator.perHour')}</span>
                        </div>
                        <div className={`text-xs mt-1 uppercase tracking-wider font-bold ${selectedPackage === pkg.id ? 'text-gold-400' : 'text-gold-700'}`}>
                          {t('calculator.afterTax')}
                        </div>
                        <div className={`text-xs mt-2 line-through ${selectedPackage === pkg.id ? 'text-gray-400' : 'text-gray-500'}`}>
                          {t('calculator.grossPerHour', { price: pkg.grossPrice })}
                        </div>
                      </div>
                      
                      <p className={`text-sm font-light leading-relaxed border-t pt-4 ${selectedPackage === pkg.id ? 'border-white/10 text-gray-300' : 'border-gray-300 text-gray-700'}`}>
                        {pkg.desc}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* STEP 2 */}
            <div className="relative">
              <svg className="absolute -left-12 top-0 hidden md:block pointer-events-none opacity-50 select-none" width="80" height="80" aria-hidden="true" focusable="false">
                <text x="0" y="60" fontFamily="serif" fontSize="60" fill="#e5e7eb">02</text>
              </svg>
              <h3 className="text-2xl font-serif mb-10 text-black-deep">{t('calculator.step2')}</h3>
              
              <div className="space-y-16">
                <div>
                  <div className="flex justify-between items-end mb-6">
                    <label id="surface-label" className="text-sm font-bold uppercase tracking-widest text-gray-600">{t('calculator.surface')}</label>
                    <span className="font-serif text-gold-600 text-5xl">{surface} <span className="text-2xl text-gray-500">{t('calculator.surfaceUnit')}</span></span>
                  </div>
                  
                  {/* Luxury Slider Customization */}
                  <div className="relative pt-4 pb-8">
                    <input 
                      type="range" min="30" max="400" step="10" value={surface}
                      onChange={(e) => setSurface(Number(e.target.value))}
                      className="w-full absolute z-20 opacity-0 cursor-pointer h-2"
                      aria-labelledby="surface-label"
                    />
                    <div className="w-full h-[2px] bg-gray-300 relative z-10 pointer-events-none">
                      <div 
                        className="absolute h-full bg-gold-600" 
                        style={{ width: `${((surface - 30) / (400 - 30)) * 100}%` }}
                      >
                        <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 w-4 h-4 bg-white border-2 border-gold-600 rounded-full shadow-md"></div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-x-12 gap-y-8">
                  {[
                    { label: t('calculator.roomsLabel'), value: rooms, setter: setRooms }, 
                    { label: t('calculator.bathroomsLabel'), value: bathrooms, setter: setBathrooms }, 
                    { label: t('calculator.kitchensLabel'), value: kitchens, setter: setKitchens }
                  ].map(item => (
                    <div key={item.label} className="border-b border-gray-300 pb-4">
                      <label className="block text-xs uppercase tracking-widest text-gray-600 mb-6 font-bold">{item.label}</label>
                      <div className="flex items-center justify-between">
                        <button onClick={() => item.setter(Math.max(1, item.value - 1))} className="text-gray-500 hover:text-gold-600 transition-colors px-2 py-1 text-2xl font-light focus:outline-none" aria-label={`Decrease ${item.label}`}>-</button>
                        <span className="font-serif text-4xl text-black-deep">{item.value}</span>
                        <button onClick={() => item.setter(item.value + 1)} className="text-gray-500 hover:text-gold-600 transition-colors px-2 py-1 text-2xl font-light focus:outline-none" aria-label={`Increase ${item.label}`}>+</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* STEP 3 */}
            <div className="relative">
              <svg className="absolute -left-12 top-0 hidden md:block pointer-events-none opacity-50 select-none" width="80" height="80" aria-hidden="true" focusable="false">
                <text x="0" y="60" fontFamily="serif" fontSize="60" fill="#e5e7eb">03</text>
              </svg>
              <h3 className="text-2xl font-serif mb-10 text-black-deep">{t('calculator.step3')}</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                {[
                  { key: 'pets', label: t('calculator.specifics.pets.label'), desc: t('calculator.specifics.pets.desc') },
                  { key: 'children', label: t('calculator.specifics.children.label'), desc: t('calculator.specifics.children.desc') },
                  { key: 'art', label: t('calculator.specifics.art.label'), desc: t('calculator.specifics.art.desc') },
                  { key: 'marble', label: t('calculator.specifics.marble.label'), desc: t('calculator.specifics.marble.desc') },
                  { key: 'dressing', label: t('calculator.specifics.dressing.label'), desc: t('calculator.specifics.dressing.desc') },
                  { key: 'silver', label: t('calculator.specifics.silver.label'), desc: t('calculator.specifics.silver.desc') },
                  { key: 'groceries', label: t('calculator.specifics.groceries.label'), desc: t('calculator.specifics.groceries.desc') },
                  { key: 'plants', label: t('calculator.specifics.plants.label'), desc: t('calculator.specifics.plants.desc') }
                ].map(item => (
                  <div 
                    key={item.key} 
                    onClick={() => setSpecifics(prev => ({...prev, [item.key]: !prev[item.key as keyof typeof specifics]}))}
                    className="group cursor-pointer flex items-start py-4 border-b border-gray-200"
                    role="checkbox"
                    aria-checked={specifics[item.key as keyof typeof specifics]}
                    tabIndex={0}
                  >
                    <div className={`mt-1 w-5 h-5 rounded flex-shrink-0 flex items-center justify-center mr-6 transition-all duration-300 ${specifics[item.key as keyof typeof specifics] ? 'bg-gold-600 shadow-[0_0_10px_rgba(201,152,85,0.4)]' : 'border border-gray-400 group-hover:border-gold-500'}`}>
                      {specifics[item.key as keyof typeof specifics] && <Check size={12} className="text-white" />}
                    </div>
                    <div>
                      <div className={`font-serif text-xl mb-1 transition-colors ${specifics[item.key as keyof typeof specifics] ? 'text-black-deep' : 'text-gray-600 group-hover:text-black-deep'}`}>
                        {item.label}
                      </div>
                      <div className="text-xs text-gray-500 font-light leading-relaxed">
                        {item.desc}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="mt-8 flex items-start text-xs text-gray-600 font-light bg-gray-100 p-6 rounded">
                <Info className="w-4 h-4 text-gray-500 mr-3 flex-shrink-0" />
                <p>{t('calculator.note')}</p>
              </div>
            </div>

          </div>

          {/* RIGHT COLUMN : STICKY LIVE SUMMARY */}
          <div className="w-full lg:w-1/3 lg:sticky lg:top-32 mt-16 lg:mt-0">
            <FadeIn>
              <div className="bg-black-deep text-white p-10 md:p-12 shadow-2xl relative overflow-hidden">
                <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-bl from-gold-500/20 to-transparent rounded-bl-full pointer-events-none"></div>
                
                <h3 className="text-sm font-bold uppercase tracking-widest text-gold-400 mb-10 flex items-center">
                  <Sparkles className="w-4 h-4 mr-3" />
                  {t('calculator.studySummary')}
                </h3>
                
                <div className="space-y-6 mb-10 pb-10 border-b border-white/10">
                  <div className="flex justify-between items-end">
                    <span className="text-gray-400 font-light">{t('calculator.estimatedTime')}</span>
                    <span className="font-serif text-2xl">{weeklyHours.toFixed(1)} <span className="text-base text-gray-500">{t('calculator.timeUnit')}</span></span>
                  </div>
                  
                  <div className="flex justify-between items-end">
                    <span className="text-gray-400 font-light">{t('calculator.serviceLevel')}</span>
                    <span className="font-serif text-lg text-right max-w-[150px] truncate">{packages.find(p => p.id === selectedPackage)?.name}</span>
                  </div>
                </div>

                <div className="mb-10">
                  <div className="text-xs uppercase tracking-widest text-gray-500 font-bold mb-3">{t('calculator.grossMonthly')}</div>
                  <div className="text-3xl font-serif text-gray-500 line-through decoration-white/20">
                    {vipMode && <span className="text-xl font-sans tracking-normal mr-2">{t('calculator.vipFrom')}</span>}
                    {grossMonthly}€
                  </div>
                </div>

                <div className="mb-12 relative">
                  <div className="inline-block px-3 py-1 bg-gold-900/50 border border-gold-500/30 rounded text-[10px] uppercase tracking-wider text-gold-400 font-bold mb-4">
                    {t('calculator.advanceBadge')}
                  </div>
                  <div className="text-sm uppercase tracking-widest text-white font-bold mb-2">{t('calculator.netMonthly')}</div>
                  <div className="text-7xl font-serif text-gold-400 tracking-tight">
                    {vipMode && <span className="text-2xl font-sans tracking-normal block text-gold-500/80 mb-2">{t('calculator.vipFrom')}</span>}
                    {taxCredit}€
                  </div>
                </div>
                
                {!hideAuditButton && (
                  <a href="/audit" className="w-full block text-center px-8 py-5 bg-white text-black-deep text-sm uppercase tracking-widest font-bold hover:bg-gold-50 hover:text-gold-600 transition-colors duration-300">
                    {t('calculator.btnAudit')}
                  </a>
                )}
                
                {vipMode ? (
                  <div className="mt-8 bg-gradient-to-br from-gold-900/60 to-black-deep border border-gold-500/40 rounded-lg p-6 relative overflow-hidden shadow-[0_0_25px_rgba(201,152,85,0.15)]">
                    <div className="absolute top-0 right-0 w-24 h-24 bg-gold-400/10 rounded-bl-full blur-2xl pointer-events-none"></div>
                    <p className="text-sm text-gray-200 text-center leading-relaxed font-light relative z-10">
                      {t('calculator.vipText')}
                      <strong className="text-gold-400 font-bold block mt-3 text-base uppercase tracking-wider">
                        {t('calculator.vipAction')}
                      </strong>
                    </p>
                  </div>
                ) : (
                  <p className="text-[10px] text-gray-500 text-center mt-6 uppercase tracking-wider">
                    {t('calculator.noCommitment')}
                  </p>
                )}
              </div>
            </FadeIn>
          </div>

        </div>
      </div>
    </section>
  );
}
